import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  ArrowDropDown,
  ArrowDropUp,
  FilterAlt,
  FilterAltOutlined,
} from "@mui/icons-material";

import { formatDate } from "../../../components/Reports/Components/utils";
import { useGlobalContext } from "../../../context/global";
import useUserDetails from "../../../helper/userData/useUserDetails";
import useInitialLoad from "../../../hooks/useInitialLoad";
import useWrapperInsightsQuery from "../../../hooks/useWrapperInsightsQuery";
import { mergeCustomOutputToAPIFiltersFormat } from "../../../helper/Quadrant/quadrantHelper";
import { processFields } from "../../../components/Filter/subComponents/filterHelper";
import {
  buildSearchTermV2WrapperExportQueryBody,
  exportSearchTermV2Report,
  getSearchTermV2VisibleColumnKeys,
} from "../../../helper/PageWiseExportLogic/searchTermV2Export";
import { openRequestTrackingPage } from "../../../helper/common/openRequestTrackingPage";

import Loader from "../../../components/loader";
import SectionTitle from "../../../components/sectionTitle";
import Breadcrumb from "../../../components/Breadcrumb/ReportsBreadCrumb";
import BulkActionBarV2 from "../../../components/Bars/BulkActionBarV2";
import BulkActionConfirmModalV2 from "../../../components/modal/BulkActionConfirmModalV2";
import AddToNegativeModalV2 from "../../../components/modal/AddToNegativeModalV2";
import CustomPagination from "../../../components/CustomPagination/CustomPagination";
import PageVersionSwitchButton from "../../../components/Buttons/PageVersionSwitchButton";
import ExportDownloadTooltip, {
  EXPORT_LIMIT_TOOLTIP_MESSAGE,
  EXPORT_TRACK_TOOLTIP_MESSAGE,
  getExportRedirectIconButtonSx,
} from "../../../components/Reports/Components/ExportDownloadTooltip";
import ComplexFilterWidget from "../../../components/Filter/ComplexFilterWidget";
import AppliedFiltersChips from "../../../components/Filter/AppliedFiltersChips";

import MasterSearchTermV2Table from "./MasterSearchTermV2Table";
import {
  FiltersCampaignDataSearchTermV2,
  FiltersDimensionDataSearchTermV2,
  FiltersMeasuresDataSearchTermV2,
  SEARCH_TERM_V2_ALWAYS_MEASURES,
  SEARCH_TERM_V2_DEFAULT_DIMENSIONS,
  SEARCH_TERM_V2_GROUP_BY_OPTIONS,
  SEARCH_TERM_V2_PAGE_MEASURES,
  getSearchTermV2Columns,
} from "./searchTermV2Configs";
import {
  buildSearchTermInsightsQueryBody,
  clearAllSelections,
  getSearchTermSearchMembers,
  injectSearchIntoFilters,
  injectSpCampaignTypeFilter,
  isCampaignTypeSP,
  normalizeSearchTermInsightRows,
  prepareWrapperFilters,
  resolveNegativeTargetType,
  SB_SD_OPTIMISATION_MODAL_NOTE,
} from "./searchTermV2LocalFilters";
import {
  BULK_SCOPE,
  applyBulkRowToggle,
  applyBulkSelectAllPage,
  applySelectAllMatching,
  clearBulkSelection,
  getRowBulkId,
  getSelectedRowIds,
  shouldShowSelectAllMatchingBanner,
} from "../../../helper/bulkSelection/bulkSelectionModes";
import { buildBulkQuery } from "../../../helper/bulkSelection/buildBulkQuery";
import {
  buildBulkNegativeTargetBody,
  getRequestedCountForBulk,
  submitBulkNegativeTargetUpdate,
} from "../../../helper/bulkSelection/bulkSearchTermNegativeUpdate";
import {
  ColumnCustomizationButton,
  COLUMN_CUSTOMIZATION_PAGE_IDS,
  V2_STANDARD_VIEW_OPTIONS,
  usePageColumnCustomization,
} from "../../../components/ColumnCustomization";
import {
  QuickFilterButton,
  QUICK_FILTER_PAGE_IDS,
} from "../../../components/QuickFilter";
import { useAuthHeader } from "react-auth-kit";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];
const REPORT_API = process.env.REACT_APP_REPORT_API;
const USER_PLATFORM = "amazon";
const EMPTY_TOPX_CONFIG = { operators: {}, fields: [] };
const SEARCH_TERM_BULK_ID_KEYS = ["search_term", "campaign_id", "ad_group_id"];

const MasterSearchTermV2 = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const location = useLocation();
  const viewTYPE =
    new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";
  const { calendarDate, applyCalendar, clientId } = useGlobalContext();
  const [userDetails] = useUserDetails();
  const [initialLoad, setInitialLoad] = useInitialLoad();
  const authHeader = useAuthHeader();
  const token = authHeader();

  const [showComplexFilter, setShowComplexFilter] = useState(false);
  const [filterModuleFilters, setFilterModuleFilters] = useState({});
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [insightsMenuAnchor, setInsightsMenuAnchor] = useState(null);
  const [showNegativeModal, setShowNegativeModal] = useState(false);
  const [bulkScope, setBulkScope] = useState(BULK_SCOPE.SELECTED);
  const [bulkExcludeIds, setBulkExcludeIds] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [pendingNegativeOperation, setPendingNegativeOperation] = useState(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [isBulkSuccess, setIsBulkSuccess] = useState(false);

  const [queryDimensions] = useState(SEARCH_TERM_V2_DEFAULT_DIMENSIONS);
  const [groupByFilter] = useState(SEARCH_TERM_V2_GROUP_BY_OPTIONS[0]?.value);
  const queryMeasure = SEARCH_TERM_V2_PAGE_MEASURES;
  const [queryOrder, setQueryOrder] = useState([["spends", "desc"]]);
  const allColumns = useMemo(() => getSearchTermV2Columns(), []);


  const {
    currentViewType,
    selectedMetricKeys,
    columnCollectionId,
    metricColumns,
    columnCollections,
    visibleTableColumns,
    isSaving: isColumnCustomizationSaving,
    syncFromViewType,
    handleApply: handleApplyColumnCustomization,
    handleSave: handleSaveColumnCustomization,
    handleSaveAs: handleSaveAsColumnCustomization,
    handleRenameView: handleRenameColumnView,
    handleDeleteView: handleDeleteColumnView,
  } = usePageColumnCustomization({
    pageId: COLUMN_CUSTOMIZATION_PAGE_IDS.SEARCH_TERM_V2,
    allColumns,
    standardViewOptions: V2_STANDARD_VIEW_OPTIONS,
    initialViewType: viewTYPE,
    platform: USER_PLATFORM,
    seedWithAllMetrics: true,
  });

  useEffect(() => {
    const viewtype =
      new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";
    syncFromViewType(viewtype);
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("viewtype", currentViewType);
    const newSearch = queryParams.toString();
    const currentSearch = window.location.search.replace(/^\?/, "");
    if (newSearch === currentSearch) return;
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${newSearch}`
    );
  }, [currentViewType]);


  const [queryTimeDimensions, setQueryTimeDimensions] = useState([
    {
      dimension: "date",
      dateRange: [
        formatDate(calendarDate[0].startDate),
        formatDate(calendarDate[0].endDate),
      ],
    },
  ]);

  const [currentPage, setCurrentPage] = useState(1);
  const [paginationLimit, setPaginationLimit] = useState(50);
  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const {
    rows: dataSource,
    setRows: setDataSource,
    summaryData,
    totalCount,
    isLoading: isLoaderVisible,
    runQuery,
    resetResult,
  } = useWrapperInsightsQuery({
    errorMessage: "Failed to fetch search terms",
  });
  const [isExportDownloading, setIsExportDownloading] = useState(false);
  const [isExportRedirectMode, setIsExportRedirectMode] = useState(false);

  const filterWidgetConfig = useMemo(
    () => ({
      dimension: FiltersDimensionDataSearchTermV2[USER_PLATFORM],
      measure: FiltersMeasuresDataSearchTermV2[USER_PLATFORM],
      campaigns: FiltersCampaignDataSearchTermV2[USER_PLATFORM],
      topXConfig: EMPTY_TOPX_CONFIG,
    }),
    []
  );

  useEffect(() => {
    const init = async () => {
      const dimensionConfig = FiltersDimensionDataSearchTermV2[USER_PLATFORM];
      const measureConfig = FiltersMeasuresDataSearchTermV2[USER_PLATFORM];
      const campaignConfig = FiltersCampaignDataSearchTermV2[USER_PLATFORM];
      if (dimensionConfig) {
        dimensionConfig.fields = await processFields(
          dimensionConfig.fields ?? [],
          clientId,
          []
        );
      }
      if (measureConfig) {
        measureConfig.fields = await processFields(
          measureConfig.fields ?? [],
          clientId,
          []
        );
      }
      if (campaignConfig) {
        campaignConfig.fields = await processFields(
          campaignConfig.fields ?? [],
          clientId,
          []
        );
      }
    };
    if (clientId) init();
  }, [clientId]);

  useEffect(() => {
    const startDate = formatDate(calendarDate[0].startDate);
    const endDate = formatDate(calendarDate[0].endDate);
    if (
      startDate !== queryTimeDimensions[0].dateRange[0] ||
      endDate !== queryTimeDimensions[0].dateRange[1]
    ) {
      setQueryTimeDimensions((prev) => {
        const data = [...prev];
        data[0] = { ...data[0], dateRange: [startDate, endDate] };
        return data;
      });
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyCalendar]);

  useEffect(() => {
    setIsExportRedirectMode(false);
  }, [filterModuleFilters, queryTimeDimensions, queryOrder, searchValue]);

  const selectedAds = useMemo(
    () => dataSource.filter((row) => row?.isChecked),
    [dataSource]
  );
  const selectedBulkIds = useMemo(
    () => getSelectedRowIds(dataSource, SEARCH_TERM_BULK_ID_KEYS),
    [dataSource]
  );
  const pageSelectableCount = useMemo(
    () => dataSource.filter((row) => isCampaignTypeSP(row?.campaign_type)).length,
    [dataSource]
  );
  const showSelectAllMatching = shouldShowSelectAllMatchingBanner({
    scope: bulkScope,
    pageSelectedCount: selectedAds.length,
    pageSelectableCount,
    totalCount,
  });

  useEffect(() => {
    setBulkScope(BULK_SCOPE.SELECTED);
    setBulkExcludeIds([]);
    setDataSource((prev) => clearAllSelections(prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    paginationLimit,
    filterModuleFilters,
    searchValue,
    queryOrder,
    queryTimeDimensions,
  ]);

  const forceExactMatchType = useMemo(() => {
    if (bulkScope !== BULK_SCOPE.SELECTED) return false;
    if (!selectedAds.length) return false;
    return selectedAds.every(
      (row) => resolveNegativeTargetType(row) === "product"
    );
  }, [bulkScope, selectedAds]);

  function getSelectedSearchDimensions() {
    return getSearchTermSearchMembers(queryDimensions);
  }

  function getPlaceholderText() {
    return "Search Search Term, Campaign Name, Ad Group, Targeting…";
  }

  function handleDataFetch(moduleFilters) {
    if (!clientId) return;

    const mergedFilters = mergeCustomOutputToAPIFiltersFormat(
      moduleFilters?.metrics?.custom_output,
      moduleFilters?.dimension?.custom_output,
      moduleFilters?.campaigns?.custom_output,
      moduleFilters?.topX?.custom_output
    );
    const { filters: transformedFilters, isEmptyMatch } =
      prepareWrapperFilters(mergedFilters);

    if (isEmptyMatch) {
      resetResult();
      setInitialLoad(false);
      return;
    }

    const filtersWithSearch = injectSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSelectedSearchDimensions()
    );

    runQuery(async ({ signal }) => {
      const queryBody = buildSearchTermInsightsQueryBody({
        queryDimensions,
        filters: filtersWithSearch,
        queryMeasure,
        queryOrder,
        dateRange: queryTimeDimensions[0]?.dateRange ?? [],
        offset: (currentPage - 1) * paginationLimit,
        limit: paginationLimit,
      });

      const summaryQueryBody = {
        query: {
          ...queryBody.query,
          measures: [
            ...new Set([...(queryMeasure || []), ...SEARCH_TERM_V2_ALWAYS_MEASURES]),
          ],
        },
      };

      const insightsUrl = `${REPORT_API}/client/${clientId}/search-term:insights?source=amazon`;
      const summaryUrl = `${REPORT_API}/client/${clientId}/search-term:insights-summary?source=amazon`;

      const [insightsRes, summaryRes] = await Promise.all([
        axios.post(insightsUrl, queryBody, { signal }),
        axios.post(summaryUrl, summaryQueryBody, { signal }),
      ]);

      const summary = summaryRes?.data ?? {};
      return {
        rows: normalizeSearchTermInsightRows(insightsRes?.data?.data ?? []),
        summaryData: summary,
        totalCount: Number(summary?.total_count) || 0,
      };
    }).finally(() => {
      setInitialLoad(false);
    });
  }

  useEffect(() => {
    handleDataFetch(filterModuleFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterModuleFilters,
    queryTimeDimensions,
    queryOrder,
    currentPage,
    paginationLimit,
    searchValue,
    searchTrigger,
    clientId,
  ]);

  const handleFilterOutput = (filters) => {
    const nextFilters = filters || {};
    setFilterModuleFilters(nextFilters);
    setIsFilterApplied(
      (nextFilters?.dimension?.data?.length ?? 0) > 0 ||
        (nextFilters?.metrics?.data?.length ?? 0) > 0
    );
    setShowComplexFilter(false);
    setCurrentPage(1);
  };

  const FilterComponent = (
    <ComplexFilterWidget
      key={`search_term_v2_${showComplexFilter}`}
      config={filterWidgetConfig}
      campaignFilter={false}
      showDimensionFilter={true}
      filters={filterModuleFilters}
      showPopupModal={showComplexFilter}
      setShowPopupModal={setShowComplexFilter}
      output={handleFilterOutput}
      prefixMember={""}
      type="page"
      sidebar={true}
      default={"metrics"}
      page={"overlay"}
      isAppliedCallback={() => {}}
      summary={"always"}
      summaryOutput={() => {}}
      calloutTitle="Select your filters"
      calloutContent="+ Add Filter"
      hideFilterSummary={true}
    />
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / paginationLimit));

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handlePageLimitChange = (event) => {
    setPaginationLimit(Number(event.target.value));
    setCurrentPage(1);
  };

  const singleCheckBoxHandler = (index) => {
    setDataSource((prev) => {
      const result = applyBulkRowToggle({
        rows: prev,
        index,
        scope: bulkScope,
        excludeIds: bulkExcludeIds,
        idKeys: SEARCH_TERM_BULK_ID_KEYS,
        isRowSelectable: (row) => isCampaignTypeSP(row?.campaign_type),
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };
  const allCheckBoxHandler = (checked) => {
    setDataSource((prev) => {
      const result = applyBulkSelectAllPage({
        rows: prev,
        checked,
        isRowSelectable: (row) => isCampaignTypeSP(row?.campaign_type),
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };
  const removeAllSelected = () => {
    setDataSource((prev) => clearBulkSelection(prev).rows);
    setBulkScope(BULK_SCOPE.SELECTED);
    setBulkExcludeIds([]);
  };

  const handleSelectAllMatching = () => {
    setDataSource((prev) => {
      const result = applySelectAllMatching({
        rows: prev,
        isRowSelectable: (row) => isCampaignTypeSP(row?.campaign_type),
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };

  const prepareBulkQuery = () => {
    const mergedFilters = mergeCustomOutputToAPIFiltersFormat(
      filterModuleFilters?.metrics?.custom_output,
      filterModuleFilters?.dimension?.custom_output,
      filterModuleFilters?.campaigns?.custom_output,
      filterModuleFilters?.topX?.custom_output
    );
    const { filters: transformedFilters } = prepareWrapperFilters(mergedFilters);
    let filtersWithSearch = injectSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSelectedSearchDimensions()
    );
    if (
      bulkScope === BULK_SCOPE.ALL_MATCHING ||
      bulkScope === BULK_SCOPE.ALL_MATCHING_EXCEPT
    ) {
      filtersWithSearch = injectSpCampaignTypeFilter(filtersWithSearch);
    }
    const excludeRows = dataSource.filter((row) =>
      bulkExcludeIds.includes(getRowBulkId(row, SEARCH_TERM_BULK_ID_KEYS))
    );
    return buildBulkQuery({
      scope: bulkScope,
      baseFilters: filtersWithSearch,
      selectedRows: selectedAds,
      excludeRows,
      idKeys: SEARCH_TERM_BULK_ID_KEYS,
      queryContext: {
        dimensions: queryDimensions,
        measures: [...queryMeasure, ...SEARCH_TERM_V2_ALWAYS_MEASURES],
        order: queryOrder,
        timeDimensions: queryTimeDimensions,
      },
    });
  };

  const handleBulkAction = (actionKey) => {
    if (actionKey !== "Add To Negative" || isBulkSubmitting) return;
    setIsBulkSuccess(false);
    setPendingNegativeOperation(null);
    setBulkConfirm(null);
    setShowNegativeModal(true);
  };

  const handleNegativeModalSubmit = (operation) => {
    setPendingNegativeOperation(operation);
    setIsBulkSuccess(false);
    setBulkConfirm({
      actionKey: "Add To Negative",
      title: "Confirm Add To Negative?",
      actionLabel: "Confirm",
      description: (
        <>
          Add selected search terms as negative targeting at{" "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {operation?.label || operation?.level}
          </Box>
          {" "}
          with match type{" "}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {operation?.matchType || "Exact"}
          </Box>
          .
        </>
      ),
    });
  };

  const handleBulkConfirm = async () => {
    if (!bulkConfirm || !pendingNegativeOperation || isBulkSubmitting) {
      if (isBulkSuccess) {
        setBulkConfirm(null);
        setIsBulkSuccess(false);
        setPendingNegativeOperation(null);
        setShowNegativeModal(false);
      }
      return;
    }

    const requestedCount = getRequestedCountForBulk({
      scope: bulkScope,
      selectedIdsCount: selectedBulkIds.length,
      totalCount,
      excludeIdsCount: bulkExcludeIds.length,
    });

    if (requestedCount <= 0) {
      toast.info("No search terms selected for Add To Negative.");
      setBulkConfirm(null);
      setPendingNegativeOperation(null);
      return;
    }

    const bulkPayload = prepareBulkQuery();
    const email = userDetails?.user?.email ?? localStorage.getItem("email") ?? "";
    const body = buildBulkNegativeTargetBody({
      level: pendingNegativeOperation.level,
      query: bulkPayload?.query,
      email,
      requestedCount,
      adjustmentType: pendingNegativeOperation.adjustmentType,
      campaignPlatformIds: pendingNegativeOperation.campaignPlatformIds,
      adsetPlatformIds: pendingNegativeOperation.adsetPlatformIds,
    });

    setIsBulkSubmitting(true);
    setInitialLoad(true);

    try {
      await submitBulkNegativeTargetUpdate({
        clientId,
        level: pendingNegativeOperation.level,
        body,
        token,
        skipEvents: true,
      });
      setPendingNegativeOperation(null);
      setShowNegativeModal(false);
      removeAllSelected();
      setIsBulkSuccess(true);
      setBulkConfirm({
        actionKey: "Add To Negative",
        title: "Request Submitted",
        actionLabel: "Close",
        description: null,
      });
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to submit Add To Negative request."
      );
      setBulkConfirm(null);
    } finally {
      setIsBulkSubmitting(false);
      setInitialLoad(false);
    }
  };

  const handleExportClick = async () => {
    if (isExportRedirectMode) {
      openRequestTrackingPage();
      return;
    }

    const exportTotalCount = totalCount || dataSource?.length || 0;
    if (exportTotalCount <= 0) {
      toast.info("No records available for export.");
      return;
    }

    const mergedFilters = mergeCustomOutputToAPIFiltersFormat(
      filterModuleFilters?.metrics?.custom_output,
      filterModuleFilters?.dimension?.custom_output,
      filterModuleFilters?.campaigns?.custom_output,
      filterModuleFilters?.topX?.custom_output
    );
    const { filters: transformedFilters, isEmptyMatch } =
      prepareWrapperFilters(mergedFilters);
    if (isEmptyMatch) {
      toast.info("No records available for export.");
      return;
    }

    const filtersWithSearch = injectSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSelectedSearchDimensions()
    );

    const insightsQueryBody = buildSearchTermInsightsQueryBody({
      queryDimensions,
      filters: filtersWithSearch,
      queryMeasure,
      queryOrder,
      dateRange: queryTimeDimensions[0]?.dateRange ?? [],
    });
    delete insightsQueryBody.query.offset;
    delete insightsQueryBody.query.limit;

    await exportSearchTermV2Report({
      clientId,
      userPlatform: USER_PLATFORM,
      triggeredBy:
        userDetails?.user?.email ?? localStorage.getItem("email") ?? "",
      totalCount: exportTotalCount,
      queryBody: buildSearchTermV2WrapperExportQueryBody({
        query: insightsQueryBody.query,
      }),
      visibleColumnKeys: getSearchTermV2VisibleColumnKeys(visibleTableColumns),
      setIsExporting: setIsExportDownloading,
      onExportSuccess: () => setIsExportRedirectMode(true),
    });
  };

  const analysisByOptions = SEARCH_TERM_V2_GROUP_BY_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box-master-search-term-v2"
    >
      <div className="campaign-list-container overflow-hidden">
        {showNegativeModal ? (
          <AddToNegativeModalV2
            open={showNegativeModal}
            clientId={clientId}
            forceExactMatchType={forceExactMatchType}
            showMatchType={!forceExactMatchType}
            infoNote={SB_SD_OPTIMISATION_MODAL_NOTE}
            onClose={() => {
              if (isBulkSubmitting) return;
              setShowNegativeModal(false);
              setPendingNegativeOperation(null);
            }}
            onSubmit={handleNegativeModalSubmit}
          />
        ) : null}

        <Box>
          <Breadcrumb />
          <Stack direction="row" alignItems="center">
            <SectionTitle sectionTitleHeading="Search Terms" />
            <PageVersionSwitchButton pageKey="search-term" isNewVersion />
          </Stack>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              mt: 1,
              position: "relative",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              options={analysisByOptions}
              disableClearable
              disabled
              getOptionLabel={(option) => option?.label || ""}
              value={
                analysisByOptions.find((option) => option.value === groupByFilter) ||
                analysisByOptions[0] ||
                null
              }
              size="small"
              sx={{
                minWidth: "200px",
                "& .MuiAutocomplete-inputRoot": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiAutocomplete-input": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiSvgIcon-root": {
                  color: "#333333",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Analysis by"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "14px",
                      color: "#333333",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#333333",
                    },
                    "& .MuiInputBase-root": {
                      height: "36px",
                      fontSize: "12px !important",
                      color: "#333333",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "12px !important",
                      color: "#333333",
                      padding: "6px 8px",
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  sx: {
                    maxHeight: 250,
                    overflow: "auto",
                    fontSize: "12px",
                    "& .MuiAutocomplete-option": {
                      fontSize: "12px",
                      color: "#333333",
                      padding: "6px 12px",
                      "&:hover": { backgroundColor: "#EDEDED" },
                    },
                  },
                },
              }}
            />

            <TextField
              size="small"
              placeholder={getPlaceholderText()}
              variant="outlined"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  setSearchValue(e.target.value);
                  setSearchTrigger((prev) => prev + 1);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "16px", color: "#333333" }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchValue("");
                        setInputValue("");
                        setCurrentPage(1);
                      }}
                      edge="end"
                    >
                      <CloseIcon sx={{ fontSize: "16px", color: "#333333" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                flexGrow: 1,
                minWidth: 200,
                "& .MuiInputBase-root": {
                  height: "36px",
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiInputBase-input": {
                  fontSize: "12px",
                  color: "#333333",
                  padding: "6px 4px",
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "12px",
                  color: "#000000",
                  opacity: 0.6,
                },
              }}
            />


            <ColumnCustomizationButton
              collections={columnCollections}
              columns={metricColumns}
              activeCollectionId={columnCollectionId}
              selectedColumnKeys={selectedMetricKeys}
              onApply={handleApplyColumnCustomization}
              onSave={handleSaveColumnCustomization}
              onSaveAs={handleSaveAsColumnCustomization}
              onRenameView={handleRenameColumnView}
              onDeleteView={handleDeleteColumnView}
              saving={isColumnCustomizationSaving}
            />

            <QuickFilterButton
              clientId={clientId}
              platform={USER_PLATFORM}
              pageId={QUICK_FILTER_PAGE_IDS.SEARCH_TERM_V2}
              filterConfig={filterWidgetConfig}
              campaignFilter={false}
              onApply={handleFilterOutput}
            />

            <ToggleButton
              value="filters"
              selected={showComplexFilter}
              onChange={() => setShowComplexFilter(!showComplexFilter)}
              sx={{
                height: "36px",
                borderRadius: 1,
                px: 1.5,
                borderColor: showComplexFilter ? "grey.700" : "grey.400",
                backgroundColor: showComplexFilter ? "grey.100" : "white",
                color: "#333333",
                fontSize: "12px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  borderColor: "grey.700",
                  backgroundColor: showComplexFilter ? "grey.200" : "grey.50",
                },
                "& .MuiTypography-root": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& svg": {
                  color: "#333333",
                },
              }}
            >
              <Badge
                overlap="circular"
                variant="dot"
                invisible={!isFilterApplied}
                sx={{
                  "& .MuiBadge-badge": {
                    bgcolor: isFilterApplied ? "primary.main" : "transparent",
                  },
                }}
              >
                {showComplexFilter ? (
                  <FilterAlt fontSize="small" />
                ) : (
                  <FilterAltOutlined fontSize="small" />
                )}
              </Badge>
              <Typography fontWeight={500} sx={{ ml: 1 }}>
                Filters
              </Typography>
              {showComplexFilter ? (
                <ArrowDropUp fontSize="small" />
              ) : (
                <ArrowDropDown fontSize="small" />
              )}
            </ToggleButton>

            <ExportDownloadTooltip
              autoOpen={isExportRedirectMode}
              title={
                isExportRedirectMode
                  ? EXPORT_TRACK_TOOLTIP_MESSAGE
                  : EXPORT_LIMIT_TOOLTIP_MESSAGE
              }
            >
              <span>
                <IconButton
                  onClick={handleExportClick}
                  disabled={isExportDownloading}
                  sx={getExportRedirectIconButtonSx(isExportRedirectMode)}
                >
                  {isExportDownloading ? (
                    <CircularProgress size={18} thickness={5} sx={{ color: "grey.600" }} />
                  ) : isExportRedirectMode ? (
                    <OpenInNew sx={{ fontSize: 20 }} />
                  ) : (
                    <FileDownloadOutlined sx={{ fontSize: 22 }} />
                  )}
                </IconButton>
              </span>
            </ExportDownloadTooltip>

            <Button
              variant="outlined"
              size="small"
              startIcon={<InsightsOutlined fontSize="small" />}
              onClick={(event) => setInsightsMenuAnchor(event.currentTarget)}
              sx={{
                height: "36px",
                textTransform: "none",
                fontSize: "12px",
                color: "#333333",
                borderColor: "grey.400",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "grey.700",
                  backgroundColor: "grey.50",
                },
              }}
            >
              View more Insights
            </Button>
            <Menu
              anchorEl={insightsMenuAnchor}
              open={Boolean(insightsMenuAnchor)}
              onClose={() => setInsightsMenuAnchor(null)}
              disableScrollLock
            >
              <MenuItem
                sx={{ fontSize: "12px" }}
                onClick={() => {
                  window.open("/search-terms-report", "_blank", "noopener,noreferrer");
                  setInsightsMenuAnchor(null);
                }}
              >
                Search Terms Report
              </MenuItem>
            </Menu>
          </Box>

          {showComplexFilter && FilterComponent}
          <AppliedFiltersChips
            filters={filterModuleFilters}
            config={filterWidgetConfig}
            onChange={handleFilterOutput}
            hideSaveActions
          />

          <BulkActionBarV2
            open={
              selectedAds.length > 0 ||
              bulkScope === BULK_SCOPE.ALL_MATCHING ||
              bulkScope === BULK_SCOPE.ALL_MATCHING_EXCEPT
            }
            scope={bulkScope}
            selectedIdsCount={selectedBulkIds.length}
            totalCount={totalCount}
            excludeIdsCount={bulkExcludeIds.length}
            showSelectAllMatching={showSelectAllMatching}
            onSelectAllMatching={handleSelectAllMatching}
            onClear={removeAllSelected}
            actions={[
              {
                key: "Add To Negative",
                label: "Add To Negative",
                variant: "contained",
                color: "primary",
              },
            ]}
            onAction={handleBulkAction}
          />

          <BulkActionConfirmModalV2
            open={Boolean(bulkConfirm)}
            title={bulkConfirm?.title}
            actionLabel={
              isBulkSubmitting
                ? "Submitting..."
                : bulkConfirm?.actionLabel || "Confirm"
            }
            description={bulkConfirm?.description}
            infoNote={SB_SD_OPTIMISATION_MODAL_NOTE}
            scope={bulkScope}
            selectedIdsCount={selectedBulkIds.length}
            totalCount={totalCount}
            excludeIdsCount={bulkExcludeIds.length}
            zIndex={1500}
            isSuccess={isBulkSuccess}
            successTitle="Request Submitted"
            successMessage="Your bulk action request has been submitted successfully. You can track it on the Request Tracking page."
            onGoToTracking={openRequestTrackingPage}
            onCancel={() => {
              if (isBulkSubmitting) return;
              setBulkConfirm(null);
              setPendingNegativeOperation(null);
              setIsBulkSuccess(false);
            }}
            onConfirm={handleBulkConfirm}
          />

          <Typography variant="caption" sx={{ color: "#333333", display: "block", mt: 0.5 }}>
            SB/SD optimisation not supported*
          </Typography>

          <MasterSearchTermV2Table
            columns={visibleTableColumns}
            rows={dataSource}
            isLoading={isLoaderVisible || initialLoad}
            queryOrder={queryOrder}
            summaryData={summaryData}
            onSortClick={(key, order) => {
              setCurrentPage(1);
              setQueryOrder([[key, order]]);
            }}
            onToggleRow={singleCheckBoxHandler}
            onToggleAll={allCheckBoxHandler}
          />

          {!isLoaderVisible &&
            !initialLoad &&
            (totalCount > 0 || dataSource?.length > 0) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#4f5366",
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>
                    Page Size
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={paginationLimit}
                      onChange={handlePageLimitChange}
                      disabled={isLoaderVisible}
                      sx={{ height: 36, fontSize: "12px", color: "#333333" }}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          sx={{ fontSize: "12px" }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  lazyLoading={isLoaderVisible}
                  totalItems={totalCount}
                  itemsPerPage={paginationLimit}
                />
              </Box>
            )}
        </Box>
      </div>
      {initialLoad ? <Loader /> : null}
    </div>
  );
};

export default MasterSearchTermV2;
